import type { JSX } from 'react'
import { useEffect, useRef } from 'react'
import { Box, Typography, Paper } from '@mui/material'
import type { UiMessage } from './useAdvisor'
import type { ToolUi } from '../runtime/types'
import { ToolChip } from './ToolChip'
import { NavProposal } from './NavProposal'
import { MarkdownMessage } from './MarkdownMessage'

/**
 * Renders the conversation as role-styled message bubbles.
 *
 * - User messages: right-aligned, primary-colour background.
 * - Assistant messages: left-aligned, neutral background.
 * - Error messages: left-aligned, error-colour background.
 * - Notice messages (e.g. "Stopped."): rendered as a subtle centered caption,
 *   not a bubble — no role-alignment, no background, no chips.
 * - Assistant non-error bubbles render their text as GFM markdown (mapped
 *   onto MUI typography via <MarkdownMessage>); user and error bubbles stay
 *   literal plain text (pre-wrap), never markdown-parsed.
 * - Chips are rendered below the message text:
 *     `nav`   → <NavProposal> with Go action
 *     `steer` → <ToolChip> with Undo affordance
 *     `chip`  → <ToolChip> label-only
 */
export function MessageList({
  messages,
  applyNav,
  applyUndo,
}: {
  messages: UiMessage[]
  applyNav: (ui: Extract<ToolUi, { kind: 'nav' }>) => void
  applyUndo: (ui: Extract<ToolUi, { kind: 'steer' }>) => void
}): JSX.Element {
  const listRef = useRef<HTMLDivElement>(null)
  // Stick to the bottom while streaming — but respect a user who scrolled up.
  const stickToBottomRef = useRef(true)

  const handleScroll = (): void => {
    const el = listRef.current
    if (!el) return
    stickToBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 40
  }

  useEffect(() => {
    const el = listRef.current
    if (el && stickToBottomRef.current) el.scrollTop = el.scrollHeight
  }, [messages])

  return (
    <Box
      ref={listRef}
      onScroll={handleScroll}
      sx={{
        flex: 1,
        overflowY: 'auto',
        p: 2,
        display: 'flex',
        flexDirection: 'column',
        gap: 1.5,
      }}
    >
      {messages.map((msg, i) => {
        if (msg.notice) {
          return (
            <Typography
              key={i}
              variant="caption"
              color="text.secondary"
              sx={{ alignSelf: 'center' }}
            >
              {msg.text}
            </Typography>
          )
        }

        return (
          <Box
            key={i}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: msg.role === 'user' ? 'flex-end' : 'flex-start',
              gap: 0.5,
            }}
          >
            <Paper
              elevation={0}
              sx={{
                px: 1.5,
                py: 1,
                maxWidth: '85%',
                bgcolor: msg.error
                  ? 'error.light'
                  : msg.role === 'user'
                    ? 'primary.main'
                    : 'grey.100',
                color: msg.error
                  ? 'error.contrastText'
                  : msg.role === 'user'
                    ? 'primary.contrastText'
                    : 'text.primary',
                borderRadius: 2,
              }}
            >
              {msg.role === 'assistant' && !msg.error ? (
                <MarkdownMessage text={msg.text} />
              ) : (
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                  {msg.text}
                </Typography>
              )}
            </Paper>
            {msg.chips !== undefined && msg.chips.length > 0 && (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 0.5,
                  maxWidth: '85%',
                }}
              >
                {msg.chips.map((chip, j) => {
                  if (chip.kind === 'nav') {
                    return <NavProposal key={j} nav={chip} onGo={applyNav} />
                  }
                  if (chip.kind === 'steer') {
                    return <ToolChip key={j} chip={chip} onUndo={applyUndo} />
                  }
                  return <ToolChip key={j} chip={chip} />
                })}
              </Box>
            )}
          </Box>
        )
      })}
    </Box>
  )
}
